import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Formik, Form, FieldArray, Field, useFormikContext, useFormik, FormikProvider } from 'formik';
import {
    Button, Card, Badge, Table, Row, Col,
    InputGroup, FormControl, Form as BootstrapForm, Spinner
} from 'react-bootstrap';
import {
    FaSave, FaTrash, FaCheckCircle,
    FaFileInvoiceDollar, FaShoppingCart, FaWrench,
    FaTruckLoading, FaUserTag, FaHashtag, FaShieldAlt,
    FaFingerprint, FaExclamationTriangle, FaInfoCircle, FaCalendarAlt
} from 'react-icons/fa';
import { BiPlus, BiTrash, BiLeftArrowAlt, BiChevronDown, BiChevronRight } from 'react-icons/bi';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

// --- CORE COMPONENTS ---
import ItemField from '../core/shared/fields/ItemField';
import SupplierField from '../core/shared/fields/SupplierField';
import CustomerSearchField from '../users/CustomerSearchField';
import DynamicDatePicker from '../core/shared/components/DynamicDatePicker';
import {
    ToolbarHeading,
    ToolbarModeToggle,
    ToolbarIdentity,
    ToolbarAgent,
    ToolbarMeta,
    ToolbarFinancials,
    ToolbarProofTrigger
} from './components/ToolbarSegments';
import SerializedItemRow from '../purchase/SerializedItemRow';
import NonSerializedItemRow from './NonSerializedItemRow';
import VariantAndUnitsModal from '../purchase/VariantAndUnitsModal';
import FormikDraftPersist from '../purchase/FormikDraftPersist';
import PurchaseReview from './components/PurchaseReview';
import QuickAgentModal from './QuickAgentModal';
import SupplierDetails from '../suppliers/SupplierDetails';
import CustomerForm from '../customers/CustomerForm';
import BrandModal from '../core/components/Brand/BrandModal';
import StockUpdateSuccessModal from '../purchase/StockUpdateSuccessModal'; // Import Success Modal
import PaymentForm from '../payments/PaymentForm'; // Import Payment Modal
import ImageUploader from '../core/components/ImageUploader/ImageUploader'; // Import ImageUploader

// --- ACTIONS & UTILS ---
import { fetchSuppliers } from '../../redux/features/supplier.features';
import { fetchCustomers } from '../../redux/features/customer.features';
import { getPurchaseItemsCatalog } from '../../redux/features/stock.features';
import apiServices from '../../services/apiServices';
import { formatCurrency, roundTo } from '../../utility';
import { getPurchaseById } from '../../redux/features/purchase.features';

const BRAND_COLOR = '#001c3a';

/* -------------------------------------------------------------------------- */
/* VALIDATION SCHEMA                                                          */
/* -------------------------------------------------------------------------- */
const validationSchema = Yup.object().shape({
    purchase_type: Yup.string().oneOf(["Supplier", "Trade-In"]).required(),
    supplier: Yup.string().when("purchase_type", {
        is: "Supplier", then: (s) => s.required("Supplier is required"),
    }),
    customer: Yup.string().when("purchase_type", {
        is: "Trade-In", then: (s) => s.required("Customer is required for Trade-In"),
    }),
    purchaseDate: Yup.date().required("Purchase date is required"),
    referenceNumber: Yup.string().required("Reference number is required"),
    purchasedItems: Yup.array().of(
        Yup.object().shape({
            item_id: Yup.string().required("Item is required."),
            purchaseQty: Yup.number().min(1, "Qty must be > 0.").required(),
            unitCost: Yup.number().min(0, "Cost cannot be negative.").required(),
            sellingPrice: Yup.number().min(0, "Price cannot be negative.").required(),
        })
    ).min(1, "At least one item is required.")
});

/* -------------------------------------------------------------------------- */
/* SUMMARY COMPONENT (Sticky Sidebar)                                         */
/* -------------------------------------------------------------------------- */
const PurchaseSummary = () => {
    const { values, setFieldValue } = useFormikContext();

    // Consolidate calculations - ONLY financial sync belongs here
    useEffect(() => {
        const subtotal = values.purchasedItems.reduce((acc, item) => {
            const lineTotal = Number(item.total_price || 0);
            return acc + lineTotal;
        }, 0);

        const discount = Number(values.purchase_discount || 0);
        const taxPercent = Number(values.purchase_tax || 0);

        const afterDiscount = subtotal - discount;
        const taxAmount = (afterDiscount * taxPercent) / 100;
        const grandTotal = roundTo(afterDiscount + taxAmount, 2);

        if (values.grand_total !== grandTotal) {
            setFieldValue("grand_total", grandTotal);
            setFieldValue("payment_due_amount", grandTotal);
        }
    }, [values.purchasedItems, values.purchase_discount, values.purchase_tax, values.grand_total, setFieldValue]);

    const itemsWithMissingCosts = useMemo(() => values.purchasedItems.filter(i => i.item_id && (!i.unitCost || i.unitCost <= 0)).length, [values.purchasedItems]);
    const itemsWithMissingSerials = useMemo(() => values.purchasedItems.filter(i => i.isSerialized && i.serializedItems?.some(s => !s.serialNumber?.trim())).length, [values.purchasedItems]);
    const duplicateSerialsCount = useMemo(() => {
        const serials = (values.purchasedItems || []).flatMap(i => i.serializedItems || []).map(s => s.serialNumber?.trim()).filter(Boolean);
        const seen = new Set();
        const duplicates = new Set();
        serials.forEach(s => seen.has(s) ? duplicates.add(s) : seen.add(s));
        return duplicates.size;
    }, [values.purchasedItems]);

    return (
        <Card className="border-0 shadow-sm sticky-top" style={{ top: 20 }}>
            <Card.Header className="bg-white border-bottom fw-black py-3 text-uppercase small text-dark d-flex align-items-center gap-2">
                <FaShieldAlt className="text-primary" /> LIVE AUDIT LOG
            </Card.Header>
            <Card.Body className="p-3">
                {/* --- GIANT GRAND TOTAL STAT --- */}
                <div className="mb-4 p-3 rounded-3 text-center" style={{ background: '#f8fafc', border: '2px solid #e2e8f0' }}>
                    <span className="stat-label d-block mb-1">Grand Total (Net)</span>
                    <div className="stat-value" style={{ fontSize: '1.8rem', color: BRAND_COLOR, letterSpacing: '-0.03em' }}>
                        {formatCurrency(values.grand_total)}
                    </div>
                </div>

                {/* --- LIVE VALIDATION CHECKLIST --- */}
                <div className="mb-4">
                    <label className="industrial-field-label mb-2">Audit Checklist</label>
                    <div className="d-flex flex-column gap-2">
                        <div className={`p-2 rounded border d-flex justify-content-between align-items-center ${itemsWithMissingCosts === 0 ? 'bg-success bg-opacity-10 border-success border-opacity-25' : 'bg-danger bg-opacity-10 border-danger border-opacity-25'}`}>
                            <span className="x-small fw-black">NO ZERO-COST ITEMS</span>
                            {itemsWithMissingCosts > 0 ? (
                                <Badge bg="danger" className="x-small shadow-sm">{itemsWithMissingCosts} ISSUE</Badge>
                            ) : (
                                <FaCheckCircle className="text-success" size={14} />
                            )}
                        </div>

                        <div className={`p-2 rounded border d-flex justify-content-between align-items-center ${itemsWithMissingSerials === 0 ? 'bg-success bg-opacity-10 border-success border-opacity-25' : 'bg-warning bg-opacity-10 border-warning border-opacity-25'}`}>
                            <span className="x-small fw-black">ALL SERIALS SCANED</span>
                            {itemsWithMissingSerials > 0 ? (
                                <Badge bg="warning" text="dark" className="x-small shadow-sm">{itemsWithMissingSerials} MISSING</Badge>
                            ) : (
                                <FaCheckCircle className="text-success" size={14} />
                            )}
                        </div>

                        <div className={`p-2 rounded border d-flex justify-content-between align-items-center ${duplicateSerialsCount === 0 ? 'bg-success bg-opacity-10 border-success border-opacity-25' : 'bg-danger bg-opacity-10 border-danger border-opacity-25'}`}>
                            <span className="x-small fw-black">DUPLICATE CONFLICTS</span>
                            {duplicateSerialsCount > 0 ? (
                                <Badge bg="danger" className="x-small shadow-sm">{duplicateSerialsCount} CONFLICTS</Badge>
                            ) : (
                                <FaCheckCircle className="text-success" size={14} />
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-auto pt-3 border-top">
                    <Row className="g-2 mb-3">
                        <Col>
                            <label className="x-small fw-bold text-muted">Discount (Rs)</label>
                        </Col>
                        <Col>
                            <Field name="purchase_discount" type="number" as={FormControl} size="sm" className="industrial-input text-end" placeholder="0.00" />
                        </Col>
                    </Row>
                    <Row className="g-2 mb-3">
                        <Col>
                            <label className="x-small fw-bold text-muted">Tax (%)</label>
                        </Col>
                        <Col>
                            <Field name="purchase_tax" type="number" as={FormControl} size="sm" className="industrial-input text-end" placeholder="0" />
                        </Col>
                    </Row>
                </div>

                <BootstrapForm.Group className="mt-3">
                    <label className="x-small fw-black text-muted text-uppercase mb-2">Private Notes</label>
                    <Field name="additional_notes" as="textarea" rows={4} className="form-control form-control-sm border-2 bg-white" placeholder="Terms, delivery instructions..." />
                </BootstrapForm.Group>
            </Card.Body>
        </Card>
    );
};


const PurchaseItemsTable = React.memo(({ values, setFieldValue, itemsLookupMap, memoizedPolicies, handleQtyChange, handleOpenVariantModal, availableItems, handleItemSelection, handleBulkApply, remove, push, isGlobalExpanded }) => {
    const duplicateSerialsMap = useMemo(() => {
        const counts = {};
        (values.purchasedItems || []).forEach(pItem => {
            (pItem?.serializedItems || []).forEach(si => {
                const sn = si.serialNumber?.trim();
                if (sn) counts[sn] = (counts[sn] || 0) + 1;
            });
        });
        return counts;
    }, [values.purchasedItems]);

    return (
        <>
            {values.purchasedItems.map((item, index) => {
                if (!item) return null;
                const lookupKey = `${item.item_id || 'new'}_${item.variant_id || ""}`;
                const itemDetails = itemsLookupMap.get(lookupKey) || null;
                const isBlank = !item.item_id;
                const isSerializedRow = item.isSerialized || (itemDetails?.category === 'Device');

                return isSerializedRow && !isBlank ? (
                    <SerializedItemRow key={`${lookupKey}-${index}`} index={index} item={item} itemDetails={itemDetails || { ...item, itemName: item.itemName }} policies={memoizedPolicies} remove={remove} onQtyChange={handleQtyChange} onEditVariant={handleOpenVariantModal} setFieldValue={setFieldValue} duplicateSerials={duplicateSerialsMap} onBulkApply={handleBulkApply} values={values} isGlobalExpanded={isGlobalExpanded} />
                ) : (
                    <NonSerializedItemRow key={`std-${index}`} item={item} index={index} isBlank={isBlank} availableItems={availableItems} onItemSelect={handleItemSelection} onQtyChange={handleQtyChange} onRemove={remove} setFieldValue={setFieldValue} isLastRow={isBlank && index === values.purchasedItems.length - 1} />
                );
            })}
            <tr>
                <td colSpan="7" className="p-2 bg-light border-0">
                    <Button variant="link" className="text-decoration-none fw-bold text-primary d-flex align-items-center gap-2 ps-3"
                        onClick={() => push({ item_id: "", variant_id: null, itemName: "", purchaseQty: 1, unitCost: 0, sellingPrice: 0, total_price: 0, isSerialized: false, serializedItems: [], condition: "Brand New" })}>
                        <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center" style={{ width: 24, height: 24 }}><BiPlus /></div>
                        Add Another Item
                    </Button>
                </td>
            </tr>
        </>
    );
});

/* -------------------------------------------------------------------------- */
/* MAIN FORM COMPONENT                                                        */
/* -------------------------------------------------------------------------- */
const UnifiedPurchaseForm = () => {
    const [searchParams] = useSearchParams();
    const { purchaseItems: availableItems } = useSelector(state => state.stockReducer);
    const { suppliers } = useSelector(state => state.supplierReducer);
    const { customers } = useSelector(state => state.customersReducer);
    const { purchaseDetails } = useSelector(state => ({
        purchaseDetails: state.purchaseReducer.purchaseDetails
    }), (prev, next) => prev.purchaseDetails === next.purchaseDetails);


    const navigate = useNavigate();
    const dispatch = useDispatch();
    const formikRef = useRef(null);

    const mode = searchParams.get('type') || 'direct';
    const editId = searchParams.get('edit');
    const isEditMode = !!editId;

    // In edit mode, we wait for purchaseDetails to resolve the correct sourceType
    const isDirect = (isEditMode && purchaseDetails && purchaseDetails._id === editId)
        ? purchaseDetails.sourceType === 'DIRECT'
        : mode === 'direct';

    const [showVariantModal, setShowVariantModal] = useState(false);
    const [showKYCModal, setShowKYCModal] = useState(false);
    const [showBillProof, setShowBillProof] = useState(false);
    const [showSupplierDetails, setShowSupplierDetails] = useState(false);
    const [currentItemIndex, setCurrentItemIndex] = useState(null);
    const [selectedBaseItem, setSelectedBaseItem] = useState(null);
    const [quickAgentModalOpen, setQuickAgentModalOpen] = useState(false);
    const [policies, setPolicies] = useState([]);

    // Post-Purchase Modal States
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [createdPurchase, setCreatedPurchase] = useState(null);
    const [verifiedItemsForModal, setVerifiedItemsForModal] = useState([]);

    // Purchase Review (Middle Man) States
    const [showReview, setShowReview] = useState(false);
    const [trackedChanges, setTrackedChanges] = useState({ added: [], modified: [], deleted: [] });
    const [pendingSubmit, setPendingSubmit] = useState(null); // Persistence for submission context

    // --- GLOBAL VIEW STATE ---
    const [isGlobalExpanded, setIsGlobalExpanded] = useState(false);
    const [globalSaving, setGlobalSaving] = useState(false);

    // 1. Stable Reference Number (Prevents reset loops)
    const generatedRefBody = useMemo(() => {
        const timestamp = Date.now().toString().slice(-6);
        const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
        return `${timestamp}${random}`;
    }, []);

    useEffect(() => {
        dispatch(fetchSuppliers());
        dispatch(fetchCustomers());
        dispatch(getPurchaseItemsCatalog());

        if (isEditMode) {
            console.log("Fetching purchase for edit:", editId);
            dispatch(getPurchaseById(editId));
        }

        // Fetch Warranty Policies
        apiServices.get('/warranty-policies')
            .then(res => setPolicies(res.data))
            .catch(err => console.error("Policy fetch failed:", err));

        // Cleanup prefill to avoid persistence on manual refresh
        if (searchParams.get('mode') === 'restock') {
            const timer = setTimeout(() => sessionStorage.removeItem('restock_prefill'), 1000);
            return () => clearTimeout(timer);
        }
    }, [dispatch, searchParams, isEditMode, editId]);



    const initialValues = useMemo(() => {
        const formatItem = (item) => {
            console.log("Formatting item:", item);
            const isSer = item.isSerialized || item.serialized || item.category === "Device";
            const mapedData = {
                item_id: item.item_id?._id || item.item_id,
                variant_id: item.variant_id?._id || item.variant_id || null,
                itemName: item.itemName || item.variant_id?.variantName || item.item_id?.itemName || "",
                itemImage: item.itemImage || item.variant_id?.variantImage || item.item_id?.itemImage || "",
                purchaseQty: item.purchaseQty || 1,
                supplier_bill_no: item.supplier_bill_no || "N/A",
                unitCost: item.unitCost || item.lastUnitCost || 0,
                sellingPrice: item.sellingPrice || item.defaultSellingPrice || 0,
                total_price: item.total_price || (item.purchaseQty * (item.unitCost || 0)),
                variantAttributes: item.variantAttributes || item.variant_id?.variantAttributes || [],
                barcode: item.barcode || item.item_id?.barcode || "",
                isVariant: item.isVariant || (item.variantAttributes && item.variantAttributes.length > 0) || false,
                isSerialized: isSer,
                warrantyPolicyId: item.warrantyPolicyId || item.item_id?.warrantyPolicyId || null,
                serializedItems: isSer
                    ? item.serializedItems?.length
                        ? item.serializedItems.map(x => (
                            {
                                ...x,
                                variant_id: item.variant_id?._id || item.variant_id || null,
                                warrantyPolicyId: x.warrantyPolicyId?._id || item.item_id?.warrantyPolicyId || null
                            }
                        ))
                        : [{
                            serialNumber: "",
                            unitCost: item.unitCost || 0,
                            sellingPrice: item.sellingPrice || 0,
                            condition: "Brand New",
                            warrantyPolicyId: item.warrantyPolicyId || item.item_id?.warrantyPolicyId || null
                        }]
                    : [],
                condition: item.condition || "Brand New",
                category: item.item_id?.category || item.category || "",
                tempId: item.tempId || `${item.item_id?._id || item.item_id}_${item.variant_id?._id || item.variant_id || 'null'}_${Math.random()}`
            };

            console.log('maped data ', mapedData)
            return mapedData
        };


        if (isEditMode && purchaseDetails && purchaseDetails._id === editId) {
            console.log("Loading existing purchase into form:", purchaseDetails);
            return {
                ...purchaseDetails,
                supplier: purchaseDetails.supplier?._id || purchaseDetails.supplier || "",
                customer: purchaseDetails.customer?._id || purchaseDetails.customer || "",
                purchaseDate: purchaseDetails.purchaseDate
                    ? purchaseDetails.purchaseDate.split('T')[0]
                    : new Date().toISOString().split('T')[0],
                purchasedItems: purchaseDetails.purchasedItems.map(formatItem)
            };
        }

        // Generate stable reference number
        const prefix = isDirect ? 'REC-' : 'PO-';
        const generatedRef = `${prefix}${generatedRefBody}`;

        // --- RESTOCK PREFILL LOGIC ---
        let prefilledItems = [];
        const isRestock = searchParams.get('mode') === 'restock';
        const supplierId = searchParams.get('supplierId') || "";
        const agentId = searchParams.get('agentId') || null;
        const agentName = searchParams.get('agentName') || "";

        if (isRestock) {
            const prefill = sessionStorage.getItem('restock_prefill');
            if (prefill) {
                try {
                    prefilledItems = JSON.parse(prefill).map(formatItem);
                    toast.info(`Pre-filled ${prefilledItems.length} items.`);
                } catch (e) {
                    console.error("Restock prefill error:", e);
                }
            }
        }

        const calculatedTotal = prefilledItems.reduce((acc, i) => acc + (Number(i.total_price) || 0), 0);

        return {
            purchase_type: "Supplier",
            supplier: supplierId,
            customer: "",
            purchaseDate: new Date().toISOString().split('T')[0],
            referenceNumber: generatedRef,
            purchase_tax: 0,
            purchase_discount: 0,
            payment_type: "Credit",
            credit_segment_name: "",
            ref_agent_id: agentId,
            ref_agent_name: agentName,
            additional_notes: "",
            purchasedItems: prefilledItems,
            grand_total: calculatedTotal,
            payment_due_amount: calculatedTotal,
            sourceType: isDirect ? 'DIRECT' : 'PO',
            isStockUpdated: isDirect,
            purchase_status: isDirect ? 'Received' : 'Pending Verification',
            isPrefilled: isRestock,
            supplier_bill_no: "",
            bill_proof: ""
        };
    }, [isDirect, searchParams, isEditMode, purchaseDetails, editId, generatedRefBody]);

    const handleFormSubmit = async (values, { setSubmitting }) => {
        if (values.purchase_type === "Trade-In") {
            const selectedCustomer = customers.find(c => c._id === values.customer);
            if (!selectedCustomer?.nic_number) {
                toast.error("Legal Compliance Error: NIC required for Trade-In.");
                setShowKYCModal(true);
                setSubmitting(false);
                return;
            }
        }

        // --- VALIDATION & AUTO-FILL (Device/Serialized Specific) ---
        let validationError = false;
        const validatedItems = values.purchasedItems.map((item, idx) => {
            // Validation: Ensure variant_id exists if it's a variant item
            const isVariantItem = item.isVariant || (item.variantAttributes && item.variantAttributes.length > 0);
            if (isVariantItem && !item.variant_id) {
                toast.error(`Item "${item.itemName}": Missing variant configuration. Please select a specific variant.`);
                validationError = true;
            }

            const isDevice = item.category === "Device" || item.isSerialized;
            if (isDevice && item.serializedItems) {
                // Mandatory Check: Direct Purchases MUST have serials for all units
                if (isDirect && (!item.serializedItems || item.serializedItems.length === 0)) {
                    toast.error(`Item ${item.itemName} is serialized but has no units defined.`);
                    validationError = true;
                }

                const updatedSerializedItems = item.serializedItems.map((unit, uIdx) => {
                    let bh = unit.batteryHealth;
                    const condition = unit.condition || item.condition || "Brand New";
                    const sn = unit.serialNumber?.trim();

                    // 1. Serial Number Validation (Strict for Direct)
                    if (isDirect && !sn) {
                        toast.error(`Item ${item.itemName}: Unit ${uIdx + 1} is missing a Serial Number.`);
                        validationError = true;
                    }

                    // 2. Battery Health Validation
                    if (!bh || bh === "" || bh === 0) {
                        if (condition === "Brand New") {
                            bh = 100; // Auto-fill 100 for brand new
                        } else {
                            if (isDirect) {
                                toast.error(`Unit ${sn || uIdx + 1} missing Battery Health. Required for ${condition} direct purchases.`);
                                validationError = true;
                            }
                        }
                    }
                    return {
                        ...unit,
                        batteryHealth: Number(bh) || 0,
                        condition,
                        serialNumber: sn,
                        variant_id: item.variant_id, // PROPROPAGATE variant_id to unit level
                        // SANITIZATION: Ensure warranty is ObjectId or Null (prevent string submission)
                        warrantyPolicyId: (unit.warrantyPolicyId && /^[0-9a-fA-F]{24}$/.test(unit.warrantyPolicyId)) ? unit.warrantyPolicyId : null
                    };
                });
                return { ...item, serializedItems: updatedSerializedItems };
            }
            return {
                ...item,
                variant_id: item.variant_id || null // Ensure null if not present
            };
        });

        if (validationError) {
            setSubmitting(false);
            return;
        }

        // --- DEFENSIVE SYNC: Recalculate Totals before Submit (Avoid race condition) ---
        const subtotal = validatedItems.reduce((acc, item) => acc + (Number(item.total_price) || 0), 0);
        const discount = Number(values.purchase_discount || 0);
        const taxPercent = Number(values.purchase_tax || 0);
        const afterDiscount = subtotal - discount;
        const taxAmount = (afterDiscount * taxPercent) / 100;
        const finalGrandTotal = roundTo(afterDiscount + taxAmount, 2);

        const submissionValues = {
            ...values,
            purchasedItems: validatedItems, // Use validated items
            grand_total: finalGrandTotal,
            payment_due_amount: finalGrandTotal // Correct for new POs
        };

        // --- INTELLIGENT CHANGE DETECTION (Middle Man) ---
        const calculateChanges = () => {
            const initial = initialValues.purchasedItems || [];
            const current = validatedItems;

            const added = [];
            const modified = [];
            const deleted = [];

            // 1. Detect Added & Modified
            current.forEach(currentItem => {
                const originalItem = initial.find(oi => oi._id === currentItem._id && oi._id);

                if (!originalItem) {
                    added.push({
                        ...currentItem,
                        currentStock: currentItem.stockCountBucket || 0
                    });
                } else {
                    const hasQtyChange = currentItem.purchaseQty !== originalItem.purchaseQty;
                    const hasCostChange = currentItem.unitCost !== originalItem.unitCost;
                    const hasPriceChange = currentItem.sellingPrice !== originalItem.sellingPrice;

                    if (hasQtyChange || hasCostChange || hasPriceChange) {
                        modified.push({
                            itemName: currentItem.itemName,
                            isSerialized: currentItem.isSerialized,
                            serials: currentItem.serializedItems || [],
                            currentStock: currentItem.stockCountBucket || 0,
                            before: {
                                qty: originalItem.purchaseQty,
                                cost: originalItem.unitCost,
                                price: originalItem.sellingPrice
                            },
                            after: {
                                qty: currentItem.purchaseQty,
                                cost: currentItem.unitCost,
                                price: currentItem.sellingPrice
                            }
                        });
                    }
                }
            });

            // 2. Detect Deleted
            initial.forEach(originalItem => {
                const stillExists = current.find(ci => ci._id === originalItem._id && ci._id);
                if (!stillExists && originalItem._id) {
                    deleted.push({
                        ...originalItem,
                        currentStock: originalItem.stockCountBucket || 0
                    });
                }
            });

            return { added, modified, deleted };
        };

        const changes = calculateChanges();

        // Save context for final commit after review
        setPendingSubmit({ submissionValues, validatedItems, setSubmitting });

        // Always show review if there's any modification/deletion or if in edit mode
        if (isEditMode || changes.added.length > 0 || changes.modified.length > 0 || changes.deleted.length > 0) {
            setTrackedChanges(changes);
            setShowReview(true);
            return; // STOP! Show Middle Man first
        }

        performFinalSubmit(submissionValues, validatedItems, { setSubmitting });
    };

    const performFinalSubmit = async (submissionValues, validatedItems, { setSubmitting }) => {
        setGlobalSaving(true);
        try {
            let res;
            if (isEditMode) {
                res = await apiServices.put(`/purchase/${editId}`, submissionValues);
                toast.success("Purchase Updated Successfully!");
                navigate('/ProcurementCenter');
                return;
            } else {
                res = await apiServices.post('/purchase', submissionValues);
            }

            if (isDirect && !isEditMode) {
                // For Direct Purchase, we show the Success Modal
                const purchaseResult = res.data.purchase || res.data;
                setCreatedPurchase(purchaseResult);

                // Construct verified items from VALIDATED form values
                const pseudoVerifiedItems = validatedItems.map(item => ({
                    ...item,
                    verifiedQty: item.purchaseQty,
                }));

                setVerifiedItemsForModal(pseudoVerifiedItems);
                setShowSuccessModal(true);
                try { localStorage.removeItem("ishop_unified_purchase_draft"); } catch (e) {}
                toast.success("Direct Purchase Processed & Stock Updated!");
            } else {
                try { localStorage.removeItem("ishop_unified_purchase_draft"); } catch (e) {}
                toast.success("Purchase Order Created!");
                navigate('/ProcurementCenter');
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Failed to save.");
            if (setSubmitting) setSubmitting(false);
        } finally {
            setGlobalSaving(false);
        }
    };


    const itemsLookupMap = useMemo(() => {
        const map = new Map();
        availableItems.forEach(i => {
            const key = `${i._id}_${i.variant_id || ""}`;
            map.set(key, i);
        });
        return map;
    }, [availableItems]);

    const handleQtyChange = React.useCallback((newQty, index, currentItem, unitIndexToRemove) => {
        const val = parseInt(newQty) || 1;
        const formik = formikRef.current; // Access the ref

        if (!formik) return;
        // 1. Update quantities using the ref's methods
        formik.setFieldValue(`purchasedItems[${index}].purchaseQty`, val);
        formik.setFieldValue(`purchasedItems[${index}].total_price`, val * (currentItem.unitCost || 0));

        // 2. Handle Serialized Items
        if (currentItem.isSerialized) {
            let currentSerials = [...(currentItem.serializedItems || [])];
            if (val > currentSerials.length) {
                const additionalCount = val - currentSerials.length;
                const newUnits = Array.from({ length: additionalCount }, (_, i) => ({
                    tempId: Date.now() + i,
                    serialNumber: "",
                    unitCost: currentItem.unitCost || 0,
                    sellingPrice: currentItem.sellingPrice || 0,
                    barcode: currentItem.barcode,
                    variant_id: currentItem?.variant_id,
                    warrantyPolicyId: currentItem?.warrantyPolicyId,
                    condition: currentItem.condition || "Brand New"
                }));
                formik.setFieldValue(`purchasedItems[${index}].serializedItems`, [...currentSerials, ...newUnits]);
            } else {
                if (typeof unitIndexToRemove === 'number') {
                    currentSerials.splice(unitIndexToRemove, 1);
                    formik.setFieldValue(`purchasedItems[${index}].serializedItems`, currentSerials);
                } else {
                    formik.setFieldValue(`purchasedItems[${index}].serializedItems`, currentSerials.slice(0, val));
                }
            }
        }
    }, []); // No dependencies needed because we use the ref!

    const handleOpenVariantModal = React.useCallback((index, details) => {
        setCurrentItemIndex(index);
        setSelectedBaseItem(details);
        setShowVariantModal(true);
    }, []); // Stable reference

    const handleBulkApply = React.useCallback((itemIndex, field) => {
        const formik = formikRef.current;
        if (!formik) return;

        const currentItem = formik.values.purchasedItems[itemIndex];
        const bulkVal = currentItem[field] || 0;

        if (currentItem.serializedItems?.length > 0) {
            const updatedSerials = currentItem.serializedItems.map(s => ({
                ...s,
                [field]: bulkVal
            }));

            formik.setFieldValue(`purchasedItems[${itemIndex}].serializedItems`, updatedSerials);

            if (field === 'unitCost') {
                formik.setFieldValue(`purchasedItems[${itemIndex}].total_price`, bulkVal * currentItem.purchaseQty);
            }

            toast.success(`Applied ${field} to all ${updatedSerials.length} units`);
        }
    }, []);

    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const suppliersMap = useMemo(() => {
        const map = new Map();
        if (suppliers) suppliers.forEach(s => map.set(s._id, s));
        return map;
    }, [suppliers]);

    const customersMap = useMemo(() => {
        const map = new Map();
        if (customers) customers.forEach(c => map.set(c._id, c));
        return map;
    }, [customers]);

    const memoizedPolicies = useMemo(() => policies, [policies.length]);

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: handleFormSubmit,
        enableReinitialize: true,
    });

    useEffect(() => {
        if (formikRef) {
            formikRef.current = formik;
        }
    }, [formik]);

    const { values, setFieldValue, setFieldError, isSubmitting, submitForm, errors, touched } = formik;


    // --- SYNC SELECTED ENTITIES ---
    useEffect(() => {
        let foundSupplier = suppliersMap.get(values?.supplier);

        // DEEP FIX: In edit mode...
        if (!foundSupplier && isEditMode && purchaseDetails?.supplier?._id === values?.supplier) {
            foundSupplier = purchaseDetails?.supplier;
            console.log("Using populated fallback for supplier:", foundSupplier?.business_name);
        }

        if (foundSupplier) {
            setSelectedSupplier(foundSupplier);
        } else if (!values?.supplier) {
            setSelectedSupplier(null);
        }

        let foundCustomer = customersMap.get(values?.customer);
        // Fallback for customer trade-ins as well
        if (!foundCustomer && isEditMode && purchaseDetails?.customer?._id === values?.customer) {
            foundCustomer = purchaseDetails.customer;
        }

        if (foundCustomer) {
            setSelectedCustomer(foundCustomer);
        } else if (!values?.customer) {
            setSelectedCustomer(null);
        }

        // --- CASCADING CLEANUP ---
        // Ensure cascading cleanup for ref_agent_id when supplier changes
        if (values?.supplier && foundSupplier && values?.ref_agent_id) {
            // Use string comparison for IDs to be safe with mixed object/string types
            const isValidAgent = foundSupplier.contacts?.some(c => (c._id?.toString() || c._id) === values?.ref_agent_id);
            if (!isValidAgent) {
                setFieldValue('ref_agent_id', '');
                setFieldValue('ref_agent_name', '');
                if (values?.credit_segment_name !== 'General' && values?.credit_segment_name !== '') {
                    setFieldValue('credit_segment_name', 'General');
                }
            }
        }
    }, [values?.supplier, values?.customer, suppliersMap, customersMap, setFieldValue, values?.ref_agent_id, purchaseDetails, isEditMode]);

    // STABILIZED HANDLERS FOR TOOLBAR RENDER ISOLATION
    const handleToggleMode = React.useCallback((mode) => {
        setFieldValue("purchase_type", mode);
        if (mode === 'Supplier') setFieldValue("customer", "");
        else setFieldValue("supplier", "");
    }, [setFieldValue]);

    const handleSelectSupplier = React.useCallback((s) => {
        setFieldValue("supplier", s._id);
        setFieldValue("business_name", s.business_name);
        setFieldValue("ref_agent_id", "");
        setFieldValue("ref_agent_name", "");
        setFieldValue("credit_segment_name", "General");
        if (!suppliersMap.has(s._id)) suppliersMap.set(s._id, s);

        // Use a stable reference to purchasedItems to avoid dependency cycle
        const items = formikRef.current?.values?.purchasedItems || [];
        const updatedItems = items.map(item => {
            if (item.serializedItems?.some(s => s.unitCost > 0)) return item;
            return { ...item, unitCost: 0, total_price: 0 };
        });
        setFieldValue("purchasedItems", updatedItems);
    }, [setFieldValue, suppliersMap]);

    const handleClearSupplier = React.useCallback(() => {
        setFieldValue("supplier", "");
        setSelectedSupplier(null);
    }, [setFieldValue]);

    const handleClearCustomer = React.useCallback(() => {
        setFieldValue("customer", "");
        setSelectedCustomer(null);
    }, [setFieldValue]);

    const handleChangeAgent = React.useCallback((selectedId) => {
        setFieldValue("ref_agent_id", selectedId);
        const agent = selectedSupplier?.contacts?.find(c => c._id === selectedId);
        if (agent) {
            setFieldValue("ref_agent_name", agent.name);
            if (selectedSupplier?.financial?.credit_segments) {
                const seg = selectedSupplier.financial.credit_segments.find(s =>
                    agent.designation?.toLowerCase().includes(s.name.toLowerCase())
                );
                if (seg) setFieldValue("credit_segment_name", seg.name);
            }
        } else {
            setFieldValue("ref_agent_name", "");
            setFieldValue("credit_segment_name", "");
        }
    }, [setFieldValue, selectedSupplier]);

    const handleUpdateField = React.useCallback((field, value) => {
        setFieldValue(field, value);
    }, [setFieldValue]);

    const handleToggleProof = React.useCallback(() => {
        setShowBillProof(prev => !prev);
    }, []);

    const handleReferenceCheck = async (e) => {
        const val = e.target.value;
        // Let Formik handle the touch state
        if (e.target.onBlur) e.target.onBlur(e);

        if (!val) return;

        try {
            // Assuming your backend has this endpoint from previous features
            const res = await apiServices.get(`/purchase/check-reference/${val}`);
            if (res.data?.exists) {
                setFieldError("referenceNumber", "⚠️ This Reference Number already exists!");
                toast.warning("Duplicate Bill Number Detected");
            }
        } catch (err) {
            console.error("Ref check failed", err);
        }
    };
    // --- ITEM LOGIC ---
    const handleItemSelection = React.useCallback((newItem, index) => {
        if (!newItem?._id) return;
        const isSer = newItem.isSerialized || newItem.serialized || newItem.category === "Device";
        const baseData = {
            item_id: newItem._id,
            variant_id: newItem.variant_id || null,
            itemName: newItem.itemName,
            itemImage: newItem.itemImage,
            purchaseQty: 1,
            unitCost: newItem.lastUnitCost || 0,
            sellingPrice: newItem.defaultSellingPrice || newItem.sellingPrice || 0,
            total_price: newItem.lastUnitCost || 0,
            variantAttributes: newItem.variantAttributes || [],
            barcode: newItem.barcode,
            isVariant: newItem.isVariant || (newItem.variantAttributes && newItem.variantAttributes.length > 0) || false,
            isSerialized: isSer,
            warrantyPolicyId: newItem.warrantyPolicyId || newItem.item_id?.warrantyPolicyId || null,
            serializedItems: isSer ? [{
                serialNumber: "",
                unitCost: newItem.lastUnitCost || 0,
                sellingPrice: newItem.defaultSellingPrice || 0,
                condition: "Brand New",
                warrantyPolicyId: newItem.warrantyPolicyId || newItem.item_id?.warrantyPolicyId || null
            }] : [],
            condition: "Brand New"
        };
        formik.setFieldValue(`purchasedItems[${index}]`, baseData);
        if (newItem.category === "Device") {
            setCurrentItemIndex(index);
            setSelectedBaseItem(newItem);
            setShowVariantModal(true);
        }
    }, [formik, setShowVariantModal, setCurrentItemIndex, setSelectedBaseItem]);

    return (
        <div className="unified-purchase-container">
            <div className="scroll-viewport">
                <FormikProvider value={formik}>
                    <Form className="w-100 mb-5">
                        <FormikDraftPersist storageKey="ishop_unified_purchase_draft" isEdit={isEditMode} />
                        <Row className="g-4">
                            {/* LEFT COLUMN: PRIMARY WORKSPACE */}
                            <Col xs={12} lg={8} xl={9} xxl={10}>
                                <Card className="border-0 shadow-sm">
                                    {/* ===== PURCHASE COMMAND TOOLBAR ===== */}
                                    <div className="purchase-command-toolbar">
                                        <ToolbarHeading isEditMode={isEditMode} isDirect={isDirect} />

                                        <ToolbarModeToggle purchaseType={values.purchase_type} onToggle={handleToggleMode} />

                                        <ToolbarIdentity
                                            purchaseType={values.purchase_type}
                                            supplierId={values.supplier}
                                            customerId={values.customer}
                                            selectedSupplier={selectedSupplier}
                                            selectedCustomer={selectedCustomer}
                                            onSelectSupplier={handleSelectSupplier}
                                            onClearSupplier={handleClearSupplier}
                                            onClearCustomer={handleClearCustomer}
                                            suppliers={suppliers}
                                            suppliersMap={suppliersMap}
                                        />

                                        <ToolbarAgent
                                            supplierId={values.supplier}
                                            agentId={values.ref_agent_id}
                                            agentName={values.ref_agent_name}
                                            creditSegment={values.credit_segment_name}
                                            selectedSupplier={selectedSupplier}
                                            onOpenAgentModal={() => setQuickAgentModalOpen(true)}
                                            onChangeAgent={handleChangeAgent}
                                        />

                                        <ToolbarMeta
                                            purchaseDate={values.purchaseDate}
                                            referenceNumber={values.referenceNumber}
                                            supplierBillNo={values.supplier_bill_no}
                                            onUpdateField={handleUpdateField}
                                            onBlurReference={handleReferenceCheck}
                                        />

                                        <ToolbarFinancials
                                            paymentType={values.payment_type}
                                            totalVolume={selectedSupplier?.totalPurchaseVolume}
                                            currentBalance={selectedSupplier?.financial?.current_balance}
                                            purchaseType={values.purchase_type}
                                            supplierId={values.supplier}
                                            setFieldValue={setFieldValue}
                                            formatCurrency={formatCurrency}
                                        />

                                        <ToolbarProofTrigger showBillProof={showBillProof} onToggle={handleToggleProof} />
                                    </div>

                                    {/* BILL PROOF PANEL */}
                                    {showBillProof && (
                                        <div className="bg-light border-bottom border-start border-end p-3 animate__animated animate__fadeIn">
                                            <ImageUploader
                                                imageUrl={values.bill_proof || ""}
                                                onImageUpload={(url) => setFieldValue("bill_proof", url)}
                                                folder="bills"
                                            />
                                        </div>
                                    )}

                                    {/* TRADE-IN KYC BADGE */}
                                    {values.purchase_type === 'Trade-In' && values.customer && !selectedCustomer?.nic_number && (
                                        <div className="p-2 bg-danger bg-opacity-10 border-bottom border-danger text-center">
                                            <Button variant="danger" size="sm" className="rounded-pill" onClick={() => setShowKYCModal(true)}>
                                                <FaFingerprint className="me-1" /> Complete KYC
                                            </Button>
                                        </div>
                                    )}

                                    <Card.Body className="p-0 pt-3">
                                        <div className="mb-3 px-3">
                                            {/* TABLE HEADER ACTIONS & STATS */}
                                            <div className="d-flex justify-content-between align-items-end mb-2 px-1">
                                                <div>
                                                    <h6 className="mb-0 fw-900 text-dark text-uppercase small tracking-wider">Line Items</h6>
                                                    <div className="d-flex align-items-center gap-2 mt-1">
                                                        <Badge bg="dark" className="rounded-pill px-2 py-1" style={{ fontSize: '0.65rem' }}>
                                                            {values?.purchasedItems?.filter(i => i.item_id).length || 0} INVENTORY ITEMS
                                                        </Badge>
                                                    </div>
                                                </div>

                                                {/* GLOBAL VIEW CONTROLS */}
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="outline-dark"
                                                        size="sm"
                                                        className="fw-900 py-0 px-2"
                                                        style={{ fontSize: '0.6rem', height: '22px', border: '1.5px solid #001c3a' }}
                                                        onClick={() => setIsGlobalExpanded(true)}
                                                    >
                                                        EXPAND ALL
                                                    </Button>
                                                    <Button
                                                        variant="dark"
                                                        size="sm"
                                                        className="fw-900 py-0 px-2"
                                                        style={{ fontSize: '0.6rem', height: '22px', backgroundColor: '#001c3a' }}
                                                        onClick={() => setIsGlobalExpanded(false)}
                                                    >
                                                        COLLAPSE ALL
                                                    </Button>
                                                </div>
                                            </div>

                                            <Card className="border-0 shadow-sm overflow-hidden rounded-3 bg-white">
                                                <Card.Body className="p-0">
                                                    <div className="table-responsive">
                                                        <Table hover className="mb-0 align-middle bulk-entry-table">
                                                            <thead>
                                                                <tr>
                                                                    <th className="ps-4">Item Description</th>
                                                                    <th className="text-center" style={{ width: 80 }}>Qty</th>
                                                                    <th className="text-center" style={{ width: 140 }}>Condition</th>
                                                                    <th className="text-end" style={{ width: 140 }}>Unit Cost</th>
                                                                    <th className="text-end" style={{ width: 140 }}>Selling Price</th>
                                                                    <th className="text-end pe-4" style={{ width: 160 }}>Total</th>
                                                                    <th style={{ width: 50 }}></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <FieldArray name="purchasedItems">
                                                                    {(fieldArrayProps) => (
                                                                        <PurchaseItemsTable
                                                                            {...fieldArrayProps}
                                                                            values={values}
                                                                            setFieldValue={setFieldValue}
                                                                            itemsLookupMap={itemsLookupMap}
                                                                            memoizedPolicies={memoizedPolicies}
                                                                            handleQtyChange={handleQtyChange}
                                                                            handleOpenVariantModal={handleOpenVariantModal}
                                                                            availableItems={availableItems}
                                                                            handleItemSelection={handleItemSelection}
                                                                            handleBulkApply={handleBulkApply}
                                                                            isGlobalExpanded={isGlobalExpanded}
                                                                        />
                                                                    )}
                                                                </FieldArray>
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* RIGHT COLUMN: HIGH-FIDELITY COMMAND SIDEBAR */}
                            <Col xs={12} lg={4} xl={3} xxl={2}>
                                <div className="command-sidebar-pane">
                                    <div className="sidebar-glass-card shadow-lg">
                                        <div className="sidebar-header">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="status-orb pulse"></div>
                                                <h6 className="mb-0 fw-900 text-uppercase tracking-wider">Fiscal Summary</h6>
                                            </div>
                                        </div>

                                        <div className="p-4">
                                            <PurchaseSummary />

                                            <div className="mt-4 pt-4 border-top border-white border-opacity-10">
                                                <label className="sidebar-label">Internal Memos</label>
                                                <div className="memo-container mt-2">
                                                    <Field
                                                        name="additional_notes"
                                                        as="textarea"
                                                        rows={6}
                                                        className="sidebar-memo-input"
                                                        placeholder="Add transaction notes..."
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>


                        {/* --- MODALS --- */}
                        < VariantAndUnitsModal
                            policies={policies}
                            show={showVariantModal}
                            onHide={() => setShowVariantModal(false)}
                            baseItem={selectedBaseItem}
                            existingData={values.purchasedItems[currentItemIndex]}
                            mode={isDirect ? 'DIRECT' : 'PO'}
                            onSave={(data) => {
                                setFieldValue(`purchasedItems[${currentItemIndex}]`, {
                                    ...values.purchasedItems[currentItemIndex],
                                    ...data,
                                    total_price: data.purchaseQty * data.unitCost
                                });
                                setShowVariantModal(false);
                            }}
                        />

                        < PurchaseReview
                            show={showReview}
                            onHide={() => setShowReview(false)}
                            values={values}
                            changes={trackedChanges}
                            isSubmitting={isSubmitting || globalSaving}
                            isEditMode={isEditMode}
                            isDirect={isDirect}
                            supplierData={selectedSupplier}
                            customerData={selectedCustomer}
                            agentData={selectedSupplier?.contacts?.find(c => (c._id?.toString() || c._id) === values.ref_agent_id)}
                            onConfirm={() => {
                                setShowReview(false);
                                if (pendingSubmit) {
                                    performFinalSubmit(
                                        pendingSubmit.submissionValues,
                                        pendingSubmit.validatedItems,
                                        { setSubmitting: pendingSubmit.setSubmitting }
                                    );
                                }
                            }}
                        />

                        < BrandModal show={showKYCModal} onHide={() => setShowKYCModal(false)} title="Identity Verification" icon={< FaFingerprint />}>
                            <CustomerForm
                                isEditMode={true}
                                selectedCustomer={customers?.find(c => c._id === values?.customer)}
                                handleClose={() => setShowKYCModal(false)}
                                savedCompleted={() => dispatch(fetchCustomers())}
                            />
                        </BrandModal >

                        {/* --- FIXED INDUSTRIAL FOOTER (Mirroring Modal Concept) --- */}
                        < div className="fixed-action-footer" >
                            <div className="d-flex align-items-center gap-4">
                                <div className="stat-block">
                                    <span className="stat-label">Items Count</span>
                                    <div className="stat-value">{(values?.purchasedItems || []).filter(i => i?.item_id).length} UNITS</div>
                                </div>
                                <div className="stat-block">
                                    <span className="stat-label">Total Cost</span>
                                    <div className="stat-value text-dark">{formatCurrency((values?.purchasedItems || []).reduce((acc, i) => acc + (Number(i?.total_price) || 0), 0))}</div>
                                </div>
                                <div className="stat-block" style={{ borderRight: 'none' }}>
                                    <span className="stat-label" style={{ color: '#1565c0' }}>Grand Total</span>
                                    <div className="stat-value" style={{ fontSize: '1.4rem', color: '#1565c0' }}>{formatCurrency(values?.grand_total || 0)}</div>
                                </div>
                            </div>

                            <div className="d-flex gap-3">
                                <Button
                                    variant="outline-danger"
                                    className="px-4 fw-bold border-2"
                                    onClick={() => { if (window.confirm('Discard all changes?')) navigate(-1); }}
                                    style={{ height: '42px' }}
                                >
                                    DISCARD
                                </Button>
                                <Button
                                    variant="dark"
                                    onClick={() => submitForm()}
                                    disabled={isSubmitting || globalSaving}
                                    className="px-5 fw-bold shadow-sm border-0"
                                    style={{ backgroundColor: BRAND_COLOR, height: '42px', fontSize: '1rem' }}
                                >
                                    {(isSubmitting || globalSaving) ? <Spinner size="sm" /> : <><FaSave className="me-2" /> FINISH & SAVE</>}
                                </Button>
                            </div>
                        </div >
                    </Form >
                </FormikProvider >
            </div >

            {/* --- SUCCESS & POST-PURCHASE MODALS --- */}
            < StockUpdateSuccessModal
                show={showSuccessModal}
                onHide={() => {
                    setShowSuccessModal(false);
                    navigate('/ProcurementCenter');
                }}
                purchase={createdPurchase}
                verifiedItems={verifiedItemsForModal}
                onMakePayment={() => {
                    setShowSuccessModal(false);
                    setShowPaymentModal(true);
                }}
            />

            < BrandModal
                show={showPaymentModal}
                onHide={() => { setShowPaymentModal(false); navigate('/ProcurementCenter'); }}
                size="fullscreen"
                title="Process Payment"
                subtitle={createdPurchase && <Badge bg="info" className="shadow-sm font-monospace">REF: {createdPurchase.referenceNumber}</Badge>}
                icon={< FaFileInvoiceDollar />}
            >
                {createdPurchase && (
                    <PaymentForm
                        propRef={createdPurchase.referenceNumber}
                        propAmount={createdPurchase.payment_due_amount}
                        propSupplier={createdPurchase.supplier}
                        hideHeader={true}
                        onSuccess={() => {
                            setShowPaymentModal(false);
                            toast.success("Payment Received & Purchase Completed!");
                            navigate('/ProcurementCenter');
                        }}
                    />
                )}
            </BrandModal >

            <QuickAgentModal
                show={quickAgentModalOpen}
                onHide={() => setQuickAgentModalOpen(false)}
                supplier={selectedSupplier}
                onSuccess={(newAgent) => {
                    if (formikRef.current) {
                        formikRef.current.setFieldValue("ref_agent_id", newAgent._id);
                        formikRef.current.setFieldValue("ref_agent_name", newAgent.name);

                        // Recalculate credit segment if needed
                        const selectedSupplier = suppliers?.find(s => s._id === formikRef.current?.values?.supplier);
                    }
                }}
            />

            <style>{`
        .industrial-strip {
          background: #f8fafc;
          border: 2px solid #001c3a;
          border-radius: 8px;
          padding: 12px 18px;
          margin-bottom: 20px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .industrial-field-label {
          font-size: 0.65rem;
          font-weight: 900;
          color: #001c3a;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 4px;
          display: block;
        }
        .bulk-entry-table thead th {
          background-color: #001c3a !important;
          border: none !important;
          color: #fff !important;
          font-weight: 800 !important;
          font-size: 0.72rem !important;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          padding: 16px 12px !important;
        }
        .bulk-entry-table thead th:first-child {
          border-top-left-radius: 8px;
        }
        .bulk-entry-table thead th:last-child {
          border-top-right-radius: 8px;
        }
        .bulk-entry-table tbody tr td {
          padding: 14px 12px !important;
          border-bottom: 1px solid #edf2f7 !important;
          vertical-align: middle;
          font-weight: 600;
        }
        .unified-purchase-container {
          height: 100vh;
          display: flex;
          flex-direction: column;
          background-color: #f4f7fa;
          overflow: hidden;
          position: relative;
        }
        .scroll-viewport {
          flex: 1;
          overflow-y: auto;
          padding: 30px;
          padding-bottom: 140px;
        }
        .command-sidebar-pane {
          position: sticky;
          top: 0;
          height: auto;
        }
        .sidebar-glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 20px;
          overflow: hidden;
        }
        .sidebar-header {
          padding: 20px 24px;
          background: #001c3a;
          color: white;
        }
        .status-orb {
          width: 8px;
          height: 8px;
          background: #4ade80;
          border-radius: 50%;
          box-shadow: 0 0 8px #4ade80;
        }
        .status-orb.pulse {
          animation: orb-pulse 2s infinite;
        }
        @keyframes orb-pulse {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(74, 222, 128, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
        }
        .sidebar-label {
          font-size: 0.65rem;
          font-weight: 900;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .sidebar-memo-input {
          width: 100%;
          background: rgba(0, 0, 0, 0.03);
          border: 1px solid rgba(0, 0, 0, 0.05);
          border-radius: 12px;
          padding: 12px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #1e293b;
          transition: all 0.2s;
        }
        .sidebar-memo-input:focus {
          background: white;
          border-color: #001c3a;
          outline: none;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }
        .purchase-command-toolbar {
          display: flex;
          align-items: center;
          background: #001c3a;
          color: white;
          border-radius: 12px 12px 0 0;
          min-height: 52px;
          overflow: hidden;
          flex-wrap: wrap !important;
          padding: 0;
          
          top: 0;
          z-index: 1001;
          box-shadow: 0 10px 30px rgba(0,0,0,0.15);
        }
        .toolbar-segment {
          padding: 8px 20px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 2px;
          flex: 1 1 auto;
          min-width: 0;
          border-left: 1px solid rgba(255,255,255,0.1);
        }
        .toolbar-segment:first-child {
          border-left: none;
        }
        .toolbar-label {
          font-size: 0.6rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: rgba(255, 255, 255, 0.45);
        }
        .toolbar-value {
          font-size: 0.85rem;
          font-weight: 800;
          color: white;
        }
        .serial-input {
          font-family: 'Courier New', Courier, monospace !important;
          font-size: 0.95rem !important;
          font-weight: 900 !important;
          color: #000000 !important;
          background-color: #fffde7 !important;
          border: 2px solid #bdbdbd !important;
          border-left: 5px solid #1565c0 !important;
          border-radius: 4px !important;
          letter-spacing: 0.08em !important;
          padding: 5px 8px !important;
          height: 36px !important;
          text-transform: uppercase !important;
        }
        .fixed-action-footer {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(20px);
          border-top: 2px solid #e2e8f0;
          padding: 16px 40px;
          z-index: 1030;
          box-shadow: 0 -15px 50px rgba(0,0,0,0.12);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .stat-block {
          border-right: 1px solid #e2e8f0;
          padding-right: 30px;
          margin-right: 30px;
        }
        .stat-label {
          font-size: 0.65rem;
          font-weight: 900;
          color: #94a3b8;
          text-transform: uppercase;
        }
        .stat-value {
          font-size: 1.35rem;
          font-weight: 900;
          color: #001c3a;
          font-family: 'JetBrains Mono', monospace;
        }


      `}</style>
        </div >
    );
};

export default UnifiedPurchaseForm;
